/**
 * The guest remediation workspace — **F22 / M19**, and the only screen in this product rendered for
 * somebody who is **not a Dedupr user**.
 *
 * ⚠️ **No shell, and that is the design rather than an omission.** No nav, no org switcher, no
 * account menu — the reader has no account and none of those would work. What replaces the shell is
 * an explanation of who we think they are and why they are seeing this at all, because an unexplained
 * page asking somebody to change their employer's CRM records reads like phishing.
 *
 * ⚠️ **The inferred-recipient caveat is the most important thing on this page.** Where the routing
 * chain matched by *name* rather than by a verified address, we may have the wrong person — and the
 * failure that matters is a wrongly-routed colleague quietly deciding somebody else's records. So it
 * is stated at the top, before the cases, with "hand it back" offered as the obvious next step rather
 * than buried among the actions.
 *
 * ⚠️ **Read-only until the code is entered**, and the page shows that as a state rather than by
 * simply omitting the buttons. Somebody who cannot find the controls assumes the link is broken.
 */
import { useEffect, useState } from 'react';
import { Button, Callout, Card, EmptyState, FieldGroup, HelpText, Input, Skeleton } from '@dedupr/ui';
import { RootCausePanel, type AttributionData } from './RootCausePanel.js';
import './Remediation.css';

interface Workspace {
  cases: string[];
  can_act: boolean;
  routed_by: string;
  inferred_recipient: boolean;
  inferred_note?: string;
  acting_as?: string;
  acting_as_verified?: boolean;
  /** F23's panel, present only where an attribution rose above the two-signal bar. */
  attributions?: AttributionData[];
  note: string;
}

const post = async (body: unknown): Promise<{ ok: boolean; data: Record<string, unknown> }> => {
  const url = new URL(window.location.href);
  const org = url.searchParams.get('org') ?? url.hash.split('org=')[1]?.split('&')[0] ?? '';
  const response = await fetch(`/v1/remediation?org_id=${encodeURIComponent(org)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return { ok: response.ok, data: ((await response.json().catch(() => ({}))) ?? {}) as Record<string, unknown> };
};

/** The token travels in the URL — it is emailed as a link, so there is nowhere else for it to be. */
function tokenFromUrl(): string {
  const url = new URL(window.location.href);
  return url.searchParams.get('t') ?? url.hash.split('t=')[1]?.split('&')[0] ?? '';
}

export function Remediation() {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Record<string, string>>({});

  const token = tokenFromUrl();

  useEffect(() => {
    let mounted = true;
    if (!token) {
      setError('This link is incomplete. Open it from the email rather than retyping it.');
      return;
    }
    void post({ token, action: 'open' }).then(({ ok, data }) => {
      if (!mounted) return;
      if (!ok) {
        /* ⚠️ The server's own words. It distinguishes unknown from revoked from expired precisely
           because this reader has no support channel, and paraphrasing would throw that away. */
        setError(String(data.detail ?? 'This link could not be opened.'));
        return;
      }
      setWorkspace(data as unknown as Workspace);
    });
    return () => {
      mounted = false;
    };
  }, [token]);

  async function verify() {
    setBusy(true);
    const { ok, data } = await post({ token, action: 'verify', otp, ...(name.trim() ? { name: name.trim() } : {}) });
    setBusy(false);
    if (!ok) {
      setError(String(data.detail ?? 'That code was not accepted.'));
      return;
    }
    setError(null);
    setWorkspace((current) => (current ? { ...current, can_act: true, ...(data as object) } : current));
  }

  async function act(caseId: string, action: 'merge' | 'keep_separate' | 'reassign') {
    setBusy(true);
    const { ok, data } = await post({ token, action, case_id: caseId });
    setBusy(false);
    if (!ok) {
      setError(String(data.detail ?? 'That could not be recorded.'));
      return;
    }
    setDone((current) => ({ ...current, [caseId]: String(data.note ?? 'Recorded.') }));
  }

  if (error && !workspace) {
    return (
      <div className="remediation remediation--bare">
        <EmptyState title="This link does not work" description={error} />
      </div>
    );
  }
  if (!workspace) return <Skeleton />;

  return (
    <div className="remediation">
      <header className="remediation__header">
        <p className="remediation__brand">dedupr</p>
        <h1>Some records look like duplicates of each other</h1>
        {/* ⚠️ Why this page exists, in the first paragraph. An unexplained page asking somebody to
            change their employer's CRM reads like phishing, and they would be right to think so. */}
        <p className="remediation__lede">
          Your organisation uses Dedupr to keep its customer records clean. These ones appear to be the
          same person or company recorded more than once, and you are listed as their owner — so you
          are better placed than anyone to say whether they are.
        </p>
      </header>

      {workspace.inferred_recipient ? (
        /* ⚠️ Before the cases, not among them. If we have the wrong person, the one thing that must
           not happen is that they decide somebody else's records because the caveat was further down. */
        <Callout tone="warning" title="We may have sent this to the wrong person">
          {/* ⚠️ The server's note is the authority on WHY, and this paragraph adds only what to DO.
              The first version repeated the reason almost word for word, which read as a stutter and
              diluted the one warning this page exists to carry — found in a browser. */}
          <p>{workspace.inferred_note}</p>
          <p>Handing it back is the safer answer and it costs nothing.</p>
        </Callout>
      ) : null}

      {error ? (
        <Callout tone="danger" title="That did not work">
          {error}
        </Callout>
      ) : null}

      {!workspace.can_act ? (
        <Card headingLevel={2} title="Confirm it is you">
          {/* ⚠️ Read-only shown as a STATE. Somebody who simply cannot find the controls assumes the
              link is broken and emails support — which, for a person with no account, means nobody. */}
          <HelpText>{workspace.note}</HelpText>
          <FieldGroup label="The 6-digit code we emailed you">
            <Input value={otp} onChange={(event) => setOtp(event.currentTarget.value)} inputMode="numeric" placeholder="000000" />
          </FieldGroup>
          <FieldGroup
            label="Your name (optional)"
            helpText="If this address is a shared mailbox, tell us who you are. We record it as something you told us rather than something we checked."
          >
            <Input value={name} onChange={(event) => setName(event.currentTarget.value)} placeholder="Ana Ozols" />
          </FieldGroup>
          <div className="remediation__actions">
            <Button onClick={verify} disabled={busy || otp.length < 6}>
              Continue
            </Button>
          </div>
        </Card>
      ) : null}

      {workspace.acting_as ? (
        <HelpText>
          Acting as <strong>{workspace.acting_as}</strong> — recorded as self-asserted, because we can
          prove the mailbox and not the person.
        </HelpText>
      ) : null}

      {workspace.cases.map((caseId) => (
        <Card key={caseId} headingLevel={2} title={`Possible duplicate · ${caseId}`}>
          {/* ⚠️ Rendered only where F23 produced one — hidden, never faked (F22's own rule). Somebody
              being asked to fix records deserves to know they look machine-made, and an empty "why"
              box would teach them the opposite. */}
          {workspace.attributions?.filter((item) => item.entity_id === caseId).map((item) => (
            <RootCausePanel key={item.entity_id} item={item} />
          ))}
          {done[caseId] ? (
            <Callout tone="success" title="Thank you — recorded">
              {done[caseId]}
            </Callout>
          ) : (
            <>
              <HelpText>
                {workspace.can_act
                  ? 'Merging keeps one record and folds the others into it. Nothing happens immediately — it goes through the same checks and the same 30-day undo window as every other change here.'
                  : 'Enter the code above to decide this one.'}
              </HelpText>
              <div className="remediation__actions">
                <Button onClick={() => act(caseId, 'merge')} disabled={!workspace.can_act || busy}>
                  These are the same
                </Button>
                <Button variant="ghost" onClick={() => act(caseId, 'keep_separate')} disabled={!workspace.can_act || busy}>
                  They are different people
                </Button>
                <Button variant="ghost" onClick={() => act(caseId, 'reassign')} disabled={!workspace.can_act || busy}>
                  Not mine — hand it back
                </Button>
              </div>
            </>
          )}
        </Card>
      ))}

      <HelpText>
        Nothing here deletes anything, and every change can be undone for 30 days. If you would rather
        not decide these, use <strong>Not mine — hand it back</strong> and they go to whoever looks
        after data at your organisation.
      </HelpText>
    </div>
  );
}
